import {
  ChangeEvent,
  ReactNode,
  useCallback,
  useState,
  useMemo,
  useId,
} from 'react';

import classNames from 'classnames';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';

import { Label, Preloader } from '../common';
import { Box } from '../../Box';
import { Button } from '../../Button';
import { Icon } from '../../Icon';

import useStyles from './MultipleTextInput.styles';

/**
 * Свойства компонента {@link MultipleTextInput}
 */
interface MultipleTextInputProps {
  /**
   * Идентификатор текстовых полей ввода.
   */
  id?: string;

  /**
   * Необязательный идентификатор для автоматического тестирования.
   */
  testId?: string;

  /**
   * Тип текстовых полей ввода (например, 'text', 'email').
   */
  type?: string;

  /**
   * Метка для группы текстовых полей ввода.
   */
  label?: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Массив текущих значений полей ввода.
   */
  values: string[];

  /**
   * Текст-заполнитель для полей ввода.
   */
  placeholder?: string;

  /**
   * Максимальное количество отображаемых полей ввода.
   */
  max?: number;

  /**
   * Функция обратного вызова, которая вызывается при изменении значения поля ввода.
   */
  onChange: (index: number, value: string) => void;

  /**
   * Функция обратного вызова, которая вызывается, когда поле ввода получает фокус.
   */
  onFocus?: () => void;

  /**
   * Функция обратного вызова, которая вызывается, когда поле ввода теряет фокус.
   */
  onBlur?: () => void;

  /**
   * Необязательная всплывающая подсказка для отображения с меткой.
   */
  tooltip?: ReactNode | null;

  /**
   * Наличие ошибки у группы текстовых полей ввода.
   */
  error?: boolean;

  /**
   * Вспомогательный текст для отображения под полями ввода.
   */
  helperText?: string;

  /**
   * Обязательность группы текстовых полей ввода.
   */
  required?: boolean;

  /**
   * Отключение группы текстовых полей ввода.
   */
  disabled?: boolean;

  /**
   * Состояние загрузки группы текстовых полей ввода.
   */
  loading?: boolean;
}

/**
 * Компонент MultipleTextInput представляет собой настраиваемое текстовое поле, которое позволяет вводить несколько текстовых значений.
 * Он поддерживает необязательные метки, всплывающие подсказки, обработку ошибок и состояния загрузки.
 *
 * @public
 * @param id - Идентификатор текстовых полей ввода.
 * @param testId - Необязательный идентификатор для автоматического тестирования.
 * @param type - Тип текстовых полей ввода (например, 'text', 'email').
 * @param label - Метка для группы текстовых полей ввода.
 * @param labelIcon - Иконка для метки.
 * @param values - Массив текущих значений полей ввода.
 * @param placeholder - Текст-заполнитель для полей ввода.
 * @param max - Максимальное количество отображаемых полей ввода.
 * @param onChange - Функция обратного вызова, которая вызывается при изменении значения поля ввода.
 * @param onFocus - Функция обратного вызова, которая вызывается, когда поле ввода получает фокус.
 * @param onBlur - Функция обратного вызова, которая вызывается, когда поле ввода теряет фокус.
 * @param tooltip - Необязательная всплывающая подсказка для отображения с меткой.
 * @param error - Наличие ошибки у группы текстовых полей ввода.
 * @param helperText - Вспомогательный текст для отображения под полями ввода.
 * @param required - Обязательность группы текстовых полей ввода.
 * @param disabled - Отключение группы текстовых полей ввода.
 * @param loading - Состояние загрузки группы текстовых полей ввода.
 * @returns Компонент MultipleTextInput.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link MultipleTextInputProps}
 */
export const MultipleTextInput = ({
  id,
  testId,
  type = 'text',
  label,
  labelIcon,
  values = [],
  placeholder,
  max = 3,
  onChange,
  onFocus = () => {},
  onBlur = () => {},
  tooltip,
  error = false,
  helperText = '',
  required = false,
  disabled = false,
  loading = false,
  ...rest
}: MultipleTextInputProps) => {
  const { classes } = useStyles();
  const reactId = useId();
  const inputId = useMemo(() => {
    return id ?? reactId;
  }, [id, reactId]);

  const [numberOfInputsShown, setNumberOfInputs] = useState(1);

  const counter = Math.max(numberOfInputsShown, values.length);
  const range = new Array(counter).fill(1);

  const handleChange = useCallback(
    (index: number) =>
      (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
        onChange(index, e.target.value),
    [onChange],
  );

  return (
    <div>
      <FormControl
        data-testid={testId}
        className={classNames(
          classes.multipleTextInput,
          'aqa_multiple_text_input',
        )}
      >
        {label && (
          <Label
            label={label}
            required={required}
            tooltip={tooltip}
            icon={labelIcon}
            inputId={inputId}
          />
        )}

        {range.map((_, index) => {
          const isButtonShown = index === counter - 1 && index + 1 < max;

          return (
            <Box
              className={classNames(isButtonShown && classes.inputWrapper)}
              mb="16"
              key={index}
              display="flex"
            >
              <Box flexGrow={1}>
                <TextField
                  {...rest}
                  id={inputId}
                  data-testid={`${testId}-index-${index + 1}`}
                  classes={{ root: classes.input }}
                  className="qaInputWrapper"
                  value={loading ? 'Загрузка...' : values[index] || ''}
                  autoComplete="off"
                  onChange={handleChange(index)}
                  onFocus={onFocus}
                  onBlur={onBlur}
                  fullWidth
                  type={type}
                  error={error}
                  helperText={helperText}
                  placeholder={placeholder || label}
                  disabled={disabled || loading}
                  slotProps={{
                    input: {
                      className: 'textFieldInput',
                      classes: { adornedEnd: classes.adornedEnd },
                      ...(loading ? { endAdornment: <Preloader /> } : {}),
                    },
                  }}
                />
              </Box>

              {isButtonShown && (
                <Box ml="20">
                  <Button
                    className={classes.addButton}
                    size="large"
                    onClick={() => setNumberOfInputs((state) => state + 1)}
                    icon={<Icon name="add_outline" size="24" />}
                    disabled={disabled || loading}
                  />
                </Box>
              )}
            </Box>
          );
        })}
      </FormControl>
    </div>
  );
};
